<template>
    <div class="step-by-step">
        <div class="step-by-step_header">
            <div class="step-by-step_progress">
                <div 
                    v-for="(step, index) in steps" 
                    :key="index"
                    class="step-item"
                    :class="{
                        'is-active': index === currentStep,
                        'is-completed': index < currentStep || completedSteps.includes(index),
                        'is-disabled': index > currentStep
                    }"
                >
                    <div class="step-item_circle">
                        <sw-icon
                            v-if="index < currentStep || completedSteps.includes(index)"
                            name="checkmark"
                        />
                        <span v-else>{{ index + 1 }}</span>
                    </div>
                    <div class="step-item_info">
                        <span class="step-item_title">{{ step.title }}</span>
                        <span class="step-item_subtitle">{{ step.subtitle }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="step-by-step_content">
            <!-- Renderiza dinamicamente todos os steps com v-show puro -->
            <div
                v-for="(step, index) in steps"
                :key="`step-container-${index}`"
                v-show="currentStep === index"
                class="step-content"
            >
                <slot
                    :name="`step-${index}`"
                    :currentStep="currentStep"
                    :nextStep="nextStep"
                    :prevStep="prevStep"
                    :canNext="canNext"
                    :canPrev="canPrev"
                    :stepIndex="index"
                />
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    steps: {
        type: Array,
        required: true,
        default: () => []
    },
    initialStep: {
        type: Number,
        default: 0
    },
    validationRules: {
        type: Object,
        default: () => ({})
    },
    completedSteps: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['step-change', 'finish', 'mark-completed']);

const currentStep = ref(props.initialStep);

const canNext = computed(() => {
    const rule = props.validationRules[currentStep.value];
    return rule ? rule() : true;
});

const canPrev = computed(() => {
    return currentStep.value > 0;
});

const nextStep = () => {
    if (canNext.value && currentStep.value < props.steps.length - 1) {
        currentStep.value++;
        emit('step-change', currentStep.value);
    }
};

const prevStep = () => {
    if (canPrev.value) {
        currentStep.value--;
        emit('step-change', currentStep.value);
    }
};

// Expor métodos para controle externo
defineExpose({
    currentStep,
    nextStep,
    prevStep,
    goToStep: (step) => {
        if (step >= 0 && step < props.steps.length) {
            currentStep.value = step;
            emit('step-change', step);
        }
    },
    markStepCompleted: (stepIndex) => {
        emit('mark-completed', stepIndex);
    }
});
</script>

<style lang="scss" scoped>
.step-by-step {
    display: flex;
    flex-direction: column;
    height: 100%;
    
    &_header {
        margin-bottom: 2em;
    }
    
    &_progress {
        display: flex;
        justify-content: space-between;
        position: relative;
        
        &::before {
            content: '';
            position: absolute;
            top: 1.5em;
            left: 2em;
            right: 2em;
            height: 2px;
            background: var(--borderColorLight);
            z-index: 1;
        }
    }
    
    &_content {
        flex: 1;
        margin-bottom: 2em;
    }
    
}

.step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;
    position: relative;
    z-index: 2;
    
    &_circle {
        width: 3em;
        height: 3em;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 0.9em;
        margin-bottom: 0.75em;
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
    
    &_info {
        display: flex;
        flex-direction: column;
        gap: 0.25em;
    }
    
    &_title {
        font-weight: 600;
        font-size: 0.85em;
        transition: all 0.3s ease;
    }
    
    &_subtitle {
        font-size: 0.7em;
        color: var(--textSecondary);
        transition: all 0.3s ease;
    }
    
    // Estados
    &.is-disabled {
        .step-item_circle {
            background: var(--backgroundCard);
            border: 2px solid var(--borderColorLight);
            color: var(--textMuted);
        }
        
        .step-item_title {
            color: var(--textMuted);
        }
        
        .step-item_subtitle {
            color: var(--textMuted);
        }
    }
    
    &.is-active {
        .step-item_circle {
            background: var(--linkColor);
            border: 2px solid var(--linkColor);
            color: white;
            transform: scale(1.1);
            box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
        }
        
        .step-item_title {
            color: var(--linkColor);
            font-weight: 700;
        }
        
        .step-item_subtitle {
            color: var(--linkColor);
            opacity: 0.8;
        }
    }
    
    &.is-completed {
        .step-item_circle {
            background: var(--colorGreen);
            border: 2px solid var(--colorGreen);
            color: white;
            box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
        }
        
        .step-item_title {
            color: var(--colorGreen);
        }
        
        .step-item_subtitle {
            color: var(--colorGreen);
            opacity: 0.8;
        }
    }
}


// Transições
.step-transition-enter-active,
.step-transition-leave-active {
    transition: all 0.4s ease;
}

.step-transition-enter-from {
    opacity: 0;
    transform: translateX(20px);
}

.step-transition-leave-to {
    opacity: 0;
    transform: translateX(-20px);
}

// Responsividade usando mixins do projeto
@import '@/assets/css/mediaScreen.scss';

@include screen_medias(s, m) {
    .step-by-step_progress {
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 1em;
        padding-bottom: 0.5em;
        
        &::before {
            display: none;
        }
    }
    
    .step-item {
        flex-direction: column;
        text-align: center;
        min-width: 80px;
        flex-shrink: 0;
        
        &_circle {
            width: 2.5em;
            height: 2.5em;
            margin-bottom: 0.5em;
        }
        
        &_info {
            flex: none;
        }
        
        &_title {
            font-size: 0.8em;
            line-height: 1.2;
        }
        
        &_subtitle {
            font-size: 0.65em;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 80px;
        }
    }
    
    .step-content {
        padding: 1.5em;
    }
}
</style>